import { inject, Injectable } from '@angular/core'
import { Observable, map } from 'rxjs'
import { ForgotPasswordService, LoginResponse, NotificationChannel, ParticipantService, SendTokenResponse, SessionService, ValidateChannelResponse } from 'superlikers-microsites-api'

import { userAdapter } from '@core/data/adapters'
import { User, Participant } from '@core/data/models'

@Injectable({
  providedIn: 'root'
})
export class AuthApi {
  #sessionService = inject(SessionService)
  #participantService = inject(ParticipantService)
  #forgotPasswordService = inject(ForgotPasswordService)

  /**
   * Fetch current authenticated user.
   * Used to establish or restore session.
   * @returns Observable<User | null> - Null if not authenticated
   */
  fetchCurrentUser(): Observable<User | null> {
    return this.#participantService.getParticipant<Participant>().pipe(
      map(response => {
        if (!response.participation.authenticated) return null

        return userAdapter(response.participation)
      })
    )
  }

  /**
   * Perform login with given credentials.
   * @param data - Login form data (e.g., { email, password })
   */
  login(data: Record<string, string>): Observable<LoginResponse> {
    return this.#sessionService.login(data)
  }

  /**
   * Logout current user. Clears session on backend.
   */
  logout(): Observable<LoginResponse> {
    return this.#sessionService.logout()
  }

  /**
   * Check session requirements. Should be called periodically after login.
   */
  checkRequirements() {
    return this.#sessionService.checkRequirements()
  }

  /**
   * Validate notification channel for password recovery.
   * Used in "Forgot Password" flow to check if email/phone is registered.
   */
  validateChannel(channel: NotificationChannel, value: string): Observable<ValidateChannelResponse> {
    return this.#forgotPasswordService.validateChannel(channel, value)
  }

  /**
   * Send password recovery token to user.
   * Used in "Forgot Password" flow to trigger email with reset instructions.
   */
  sendToken(channel: NotificationChannel, data: Record<string, string>, redirectUrl: string): Observable<SendTokenResponse> {
    return this.#forgotPasswordService.sendToken(channel, data, redirectUrl)
  }
}
